// src/core/stats.ts
import type { Nature, StatBlock, StatKey } from './types';
/** 空面板（全 0） */
export const EMPTY_STATS: StatBlock = {
  hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0,
};
/** 默认个体值（满个体，按你当前 UI 上限 60 来设） */
export const DEFAULT_IVS: StatBlock = {
  hp: 60, atk: 60, def: 60, spa: 60, spd: 60, spe: 60,
};
/** 默认努力值（PvP 公式已不用，保留字段兼容旧代码） */
export const DEFAULT_EVS: StatBlock = {
  hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0,
};
/** 对战固定 60 级 */
export const PVP_LEVEL = 60;
/** 单项个体值上限 */
export const EV_MAX = 60;
/** 个体值总和上限（6 × 60 = 360） */
export const EV_TOTAL_MAX = 360;
/** 种族值总和参考上限（用于进度条展示，可按实际最大值调整） */
export const RACE_TOTAL_MAX = 1000;

/**
 * 核心：每级加值
 * 公式：每级加值 = 0.01 × 种族资质 + 0.005 × 个体资质
 */
export function getPerLevelValue(base: number, iv: number): number {
  return 0.01 * base + 0.005 * iv;
}

/**
 * 性格修正系数
 * 无修正 → 1；+20% → 1.2；-10% → 0.9
 */
export function getNatureModifier(
  nature: Nature | undefined,
  key: StatKey,
): number {
  if (!nature) return 1;
  if (nature.plus === key) return 1.2;
  if (nature.minus === key) return 0.9;
  return 1;
}

/**
 * PvP 生命
 * 公式：(70 + 每级加值 × 170) × 性格修正 + 100
 * 先对(70 + perLevel*170)四舍五入；再乘性格+100，再四舍五入
 */
export function calcPvpHp(
  base: number,
  iv: number,
  natureMod: number,
): number {
  const perLevel = getPerLevelValue(base, iv);
  const step1 = Math.round(70 + perLevel * 170);
  return Math.round(step1 * natureMod + 100);
}

/**
 * PvP 普通五维：物攻 / 魔攻 / 速度 沿用此函数
 * 公式：(10 + 每级加值 × 110) × 性格修正 + 50
 * 先对(10 + perLevel*110)四舍五入；再乘性格+50，再四舍五入
 */
export function calcPvpOtherStat(
  base: number,
  iv: number,
  natureMod: number,
): number {
  const perLevel = getPerLevelValue(base, iv);
  const step1 = Math.round(10 + perLevel * 110);
  return Math.round(step1 * natureMod + 50);
}

/**
 * 【新增】物防、魔防专用计算函数
 * 规则：
 * iv=0 → Math.floor（直接去除小数）
 * iv=60 → Math.round（四舍五入）
 * 0<iv<60：在 floor 和 round结果之间线性插值
 */
export function calcPvpDefStat(
  base: number,
  iv: number,
  natureMod: number,
): number {
  const perLevel = getPerLevelValue(base, iv);
  const step1 = 10 + perLevel * 110;
  const val = step1 * natureMod + 50;

  const floorVal = Math.floor(val);
  const roundVal = Math.round(val);

  // iv权重，0~60映射0~1
  const weight = Math.max(0, Math.min(1, iv / 60));
  const mixed = floorVal + (roundVal - floorVal) * weight;

  return Math.round(mixed);
}

/**
 * 主入口：计算最终面板
 * 对战固定 60 级
 * atk/spa/spe → calcPvpOtherStat
 * def/spd → calcPvpDefStat（特殊取整规则）
 */
export function calculateStats(
  base: StatBlock,
  ivs: StatBlock,
  _evs: StatBlock,
  nature: Nature | undefined,
  _level: number = PVP_LEVEL,
): StatBlock {
  return {
    hp: calcPvpHp(base.hp, ivs.hp, getNatureModifier(nature, 'hp')),
    atk: calcPvpOtherStat(base.atk, ivs.atk, getNatureModifier(nature, 'atk')),
    def: calcPvpDefStat(base.def, ivs.def, getNatureModifier(nature, 'def')),
    spa: calcPvpOtherStat(base.spa, ivs.spa, getNatureModifier(nature, 'spa')),
    spd: calcPvpDefStat(base.spd, ivs.spd, getNatureModifier(nature, 'spd')),
    spe: calcPvpOtherStat(base.spe, ivs.spe, getNatureModifier(nature, 'spe')),
  };
}

/** 校验个体值是否超单项上限 */
export function isEvValid(evs: StatBlock): boolean {
  return (Object.keys(evs) as StatKey[]).every(
    (k) => evs[k] >= 0 && evs[k] <= EV_MAX,
  );
}

/** 校验个体值总和是否超上限 */
export function isEvTotalValid(evs: StatBlock): boolean {
  const total = (Object.keys(evs) as StatKey[]).reduce(
    (sum, k) => sum + evs[k],
    0,
  );
  return total <= EV_TOTAL_MAX;
}

/** 生成默认个体值（满个体） */
export function makeDefaultIvs(): StatBlock {
  return { ...DEFAULT_IVS };
}

/** 生成默认努力值（全 0） */
export function makeDefaultEvs(): StatBlock {
  return { ...DEFAULT_EVS };
}
